import React from 'react'
import { Alert, Box, Button, Chip, Stack, TextField, Tooltip, Typography } from '@mui/material'

import { getPennylaneCustomer, setPennylaneCustomer } from '../../../Services/clientInvoices'

interface Props {
	billingAccountId: string
	/** Rechargement des factures après rattachement : le prochain import les remontera. */
	onLinked?: () => void
}

/**
 * Rattachement du compte à son client Pennylane.
 *
 * ⚠️ Ce champ est la CONDITION de tout l'import : sans lui, aucune facture n'est rapatriée
 * pour ce compte, et son partenaire n'est jamais commissionné — sans qu'aucune erreur ne
 * le signale. C'est pour ça qu'il vit ici, en tête du bloc Factures, et pas enfoui dans un
 * écran de réglages.
 *
 * ⚠️ La valeur attendue est le `customer.id` de l'API Pennylane v2 (un entier), lisible
 * dans l'URL de la fiche client. PAS l'UUID de `pennylane_id`, vestige de 2026-04.
 */
export const PennylaneLinkField: React.FC<Props> = ({ billingAccountId, onLinked }) => {
	const [current, setCurrent] = React.useState<number | null>(null)
	const [value, setValue] = React.useState('')
	const [busy, setBusy] = React.useState(false)
	const [error, setError] = React.useState<string | null>(null)
	const [saved, setSaved] = React.useState(false)

	const load = React.useCallback(() => {
		getPennylaneCustomer(billingAccountId)
			.then((r) => {
				setCurrent(r.pennylaneCustomerId || 0)
				setValue(r.pennylaneCustomerId ? String(r.pennylaneCustomerId) : '')
			})
			.catch(() => setCurrent(0))
	}, [billingAccountId])

	React.useEffect(load, [load])

	const save = async () => {
		const parsed = value.trim() === '' ? 0 : Number(value.trim())
		if (!Number.isInteger(parsed) || parsed < 0) {
			setError('Identifiant client Pennylane invalide (un entier)')
			return
		}
		setBusy(true)
		setError(null)
		setSaved(false)
		try {
			await setPennylaneCustomer(billingAccountId, parsed)
			setCurrent(parsed)
			setSaved(true)
			onLinked?.()
		} catch (e: any) {
			setError(e?.message || 'Rattachement impossible')
		} finally {
			setBusy(false)
		}
	}

	const dirty = String(current ?? '') !== value.trim() && !(current === 0 && value.trim() === '')

	return (
		<Box sx={{ mt: 1 }}>
			<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
				<Tooltip title="Le customer.id de l'API Pennylane v2, visible dans l'URL de la fiche client (/customers/<id>). Sans lui, aucune facture n'est importée pour ce compte.">
					<TextField
						size="small"
						label="Client Pennylane"
						placeholder="ex. 134165379"
						value={value}
						onChange={(e) => setValue(e.target.value.replace(/\D/g, ''))}
						sx={{ width: 200 }}
					/>
				</Tooltip>
				<Button size="small" variant="outlined" disabled={busy || !dirty} onClick={save}>
					{current ? 'Modifier' : 'Rattacher'}
				</Button>
				{current ? (
					<Chip size="small" color="success" variant="outlined" label="Rattaché" />
				) : (
					<Tooltip title="Tant que ce compte n'est pas rattaché, l'import ne rapatrie aucune facture et son partenaire n'est pas commissionné.">
						<Chip size="small" color="warning" label="Non rattaché" />
					</Tooltip>
				)}
				{saved && (
					<Typography variant="caption" color="success.main">
						Enregistré — les factures remonteront à la prochaine synchronisation.
					</Typography>
				)}
			</Stack>
			{error && (
				<Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
					{error}
				</Alert>
			)}
		</Box>
	)
}
